import React, { useEffect } from 'react'
import { useToast } from './ToastProvider'

export function isTransientAuthNetworkError(error) {
  const message = (error?.message || error?.toString?.() || '').toLowerCase()
  const name = (error?.name || '').toLowerCase()

  return (
    name === 'typeerror' ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('connection') ||
    message.includes('err_name_not_resolved') ||
    message.includes('dns') ||
    message.includes('timeout')
  )
}

function getAuthErrorDisplayMessage(error) {
  const message = error?.message || error?.toString() || 'Unknown error'
  const normalizedMessage = message.toLowerCase()

  if (isTransientAuthNetworkError(error)) {
    return {
      title: 'Temporary connection issue',
      message: 'We had trouble reaching secure sign-in. Please wait a moment and try again. Your account is safe.',
      inline: 'Secure sign-in is temporarily unreachable. Please try again in a moment.'
    }
  }

  if (normalizedMessage.includes('identifier_not_found') || normalizedMessage.includes('user not found')) {
    return {
      title: 'Account Not Found',
      message: 'No account found with this email address. Please check your email or create a new account.',
      inline: 'No account found with this email address.'
    }
  }

  if (normalizedMessage.includes('password_incorrect') || normalizedMessage.includes('password incorrect')) {
    return {
      title: 'Incorrect Password',
      message: 'The password you entered is incorrect. Please try again or use "Forgot Password" to reset it.',
      inline: 'Incorrect password. Please try again.'
    }
  }

  if (normalizedMessage.includes('identifier_exists') || normalizedMessage.includes('already exists')) {
    return {
      title: 'Account Already Exists',
      message: 'An account with this email already exists. Please sign in instead or use a different email address.',
      inline: 'An account with this email already exists.'
    }
  }

  if (normalizedMessage.includes('password_validation_failed')) {
    return {
      title: 'Password Too Weak',
      message: 'Please choose a stronger password with at least 8 characters, including uppercase, lowercase, numbers, and symbols.',
      inline: 'Password must meet security requirements.'
    }
  }

  if (normalizedMessage.includes('identifier_invalid')) {
    return {
      title: 'Invalid Email',
      message: 'Please enter a valid email address.',
      inline: 'Please enter a valid email address.'
    }
  }

  return {
    title: 'Authentication Error',
    message: 'Unable to complete your request. Please try again or contact support if the problem persists.',
    inline: 'Please check your information and try again.'
  }
}

// Professional error handling that actually works with Supabase Auth
export function useAuthErrorHandler() {
  const { addToast } = useToast()

  const handleAuthError = (error) => {
    console.log('Auth error received:', error)
    
    // Extract error details
    const primaryError = error?.errors?.[0]
    const errorMessage = primaryError?.message || error?.message || error?.toString() || 'Unknown error'
    const errorCode = primaryError?.code || error?.code || error?.status || error?.statusCode || ''
    const normalizedMessage = errorMessage.toLowerCase()
    
    // Handle 400 Bad Request errors specifically
    if (errorCode === 400 || errorCode === '400' || error?.status === 400 || error?.statusCode === 400) {
      console.error('[AUTH ERROR] 400 Bad Request detected:', {
        error,
        primaryError,
        errorMessage,
        errorCode,
        fullError: JSON.stringify(error, null, 2)
      })
      
      // Check for specific 400 error types
      if (normalizedMessage.includes('invalid') || normalizedMessage.includes('bad request')) {
        addToast({
          type: 'error',
          title: 'Invalid Request',
          message: 'The sign-in request was invalid. Please check your information and try again. If this persists, please refresh the page.',
          duration: 8000
        })
      } else if (normalizedMessage.includes('path') || normalizedMessage.includes('routing')) {
        addToast({
          type: 'error',
          title: 'Configuration Error',
          message: 'There was a configuration issue. Please refresh the page and try again.',
          duration: 8000
        })
      } else {
        addToast({
          type: 'error',
          title: 'Sign-In Failed',
          message: 'Unable to sign in. Please check your email and password, or try refreshing the page.',
          duration: 8000
        })
      }
      return
    }

    if (errorCode === 'session_exists' || normalizedMessage.includes('already signed in') || normalizedMessage.includes('session already exists')) {
      addToast({
        type: 'info',
        title: 'Already Signed In',
        message: 'You are already signed in. Continuing to your account...',
        duration: 4000
      })
      return
    }
    
    // Handle specific error patterns
    if (normalizedMessage.includes('identifier_not_found') || normalizedMessage.includes('user not found') || normalizedMessage.includes('not found')) {
      addToast({
        type: 'error',
        title: 'Account Not Found',
        message: 'No account found with this email address. Please check your email or create a new account.',
        duration: 6000
      })
      return
    }
    
    if (normalizedMessage.includes('password_incorrect') || normalizedMessage.includes('password incorrect') || normalizedMessage.includes('invalid password')) {
      addToast({
        type: 'error',
        title: 'Incorrect Password',
        message: 'The password you entered is incorrect. Please try again or use "Forgot Password" to reset it.',
        duration: 6000
      })
      return
    }
    
    if (normalizedMessage.includes('identifier_exists') || normalizedMessage.includes('already exists')) {
      addToast({
        type: 'error',
        title: 'Account Already Exists',
        message: 'An account with this email already exists. Please sign in instead or use a different email address.',
        duration: 8000
      })
      return
    }
    
    if (normalizedMessage.includes('password_validation_failed') || normalizedMessage.includes('password requirements')) {
      addToast({
        type: 'error',
        title: 'Password Too Weak',
        message: 'Please choose a stronger password with at least 8 characters, including uppercase, lowercase, numbers, and symbols.',
        duration: 8000
      })
      return
    }
    
    if (normalizedMessage.includes('identifier_invalid') || normalizedMessage.includes('invalid email')) {
      addToast({
        type: 'error',
        title: 'Invalid Email',
        message: 'Please enter a valid email address (e.g., user@example.com).',
        duration: 5000
      })
      return
    }
    
    // Handle HTTP status codes
    if (errorCode === 422 || errorCode === '422') {
      if (normalizedMessage.includes('password') || normalizedMessage.includes('credentials')) {
        addToast({
          type: 'error',
          title: 'Incorrect Password',
          message: 'The password you entered is incorrect. Please try again or use "Forgot Password" to reset it.',
          duration: 6000
        })
      } else if (normalizedMessage.includes('email') || normalizedMessage.includes('identifier')) {
        addToast({
          type: 'error',
          title: 'Account Not Found',
          message: 'No account found with this email address. Please check your email or create a new account.',
          duration: 6000
        })
      } else {
        addToast({
          type: 'error',
          title: 'Invalid Information',
          message: 'Please check your information and try again.',
          duration: 6000
        })
      }
      return
    }
    
    if (errorCode === 429 || errorCode === '429' || normalizedMessage.includes('429')) {
      addToast({
        type: 'error',
        title: 'Too Many Requests',
        message: 'Too many failed attempts. Please wait a few minutes before trying again.',
        duration: 8000
      })
      return
    }
    
    // Handle transient auth network errors before showing credential-related copy.
    if (isTransientAuthNetworkError(error)) {
      const display = getAuthErrorDisplayMessage(error)
      addToast({
        type: 'error',
        title: display.title,
        message: display.message,
        duration: 10000,
        dedupeKey: 'auth-network-error'
      })
      return
    }
    
    // Default error message
    addToast({
      type: 'error',
      title: 'Authentication Error',
      message: 'Unable to complete your request. Please try again or contact support if the problem persists.',
      duration: 6000
    })
  }

  return { handleAuthError }
}

function isLikelyAuthErrorText(errorString) {
  const lower = (errorString || '').toLowerCase()

  return (
    lower.includes('supabase') ||
    lower.includes('authapierror') ||
    lower.includes('authretryablefetcherror') ||
    lower.includes('/auth/') ||
    lower.includes('grant_type=refresh_token') ||
    lower.includes('sign-in') ||
    lower.includes('sign in') ||
    lower.includes('signin') ||
    lower.includes('login') ||
    lower.includes('password_incorrect') ||
    lower.includes('identifier_not_found') ||
    lower.includes('identifier_invalid') ||
    lower.includes('identifier_exists') ||
    lower.includes('password_validation_failed')
  )
}

// Global error interceptor that actually works
export function useGlobalAuthErrorInterceptor() {
  const { handleAuthError } = useAuthErrorHandler()

  useEffect(() => {
    // Intercept unhandled promise rejections (common surface for auth SDK errors)
    const handleUnhandledRejection = (event) => {
      const error = event.reason
      if (!error) return
      
      const errorString = error.toString()
      
      // Check if this is an auth-related error
      if (isLikelyAuthErrorText(errorString)) {
        
        console.log('Intercepted auth error:', errorString)
        
        // Prevent the default browser error handling
        event.preventDefault()
        
        // Handle the error
        handleAuthError(error)
      }
    }

    // Intercept console errors that might be from Supabase Auth
    const originalError = console.error
    console.error = (...args) => {
      originalError.apply(console, args)
      
      const errorString = args.join(' ')
      
      // Check if this is an auth-related error
      if (isLikelyAuthErrorText(errorString)) {
        
        // Don't process our own error messages
        if (errorString.includes('Auth error received:')) return
        
        console.log('Intercepted console error:', errorString)
        handleAuthError(new Error(errorString))
      }
    }

    // Note: fetch interception is handled by NetworkErrorHandler component
    // which properly excludes Supabase API calls from generic error handling

    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      console.error = originalError
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [handleAuthError])
}

// Component for displaying error messages inline
export function AuthErrorDisplay({ error, className = '' }) {
  if (!error) return null

  const display = getAuthErrorDisplayMessage(error)

  return (
    <div className={`text-red-600 text-sm mt-2 p-3 bg-red-50 border border-red-200 rounded-md ${className}`}>
      <div className="flex items-start">
        <svg className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <span>{display.inline}</span>
      </div>
    </div>
  )
}
